import { WithDisposable } from '@blocksuite/block-std';
import { LitElement, css, html, nothing } from 'lit';

const getCollapseChevronIcon = (color: string) => {
  const cssColor = color.startsWith('--') ? `var(${color})` : color;
  return html`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" fill="none"><path d="M9 6L15 12L9 18" stroke="${cssColor}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
};
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { styleMap } from 'lit/directives/style-map.js';

import type { EdgelessRootBlockComponent } from '../../edgeless-root-block.js';
import {
  MindmapElementModel,
  type MindmapNode,
} from '../../../../surface-block/element-model/mindmap.js';
import { LayoutType } from '../../../../surface-block/element-model/utils/mindmap/layout.js';
import { ShapeElementModel } from '../../../../surface-block/index.js';

type CollapseButtonInfo = {
  id: string;
  nodeId: string;
  mindmap: MindmapElementModel;
  node: MindmapNode;
  count: number;
  x: number;
  y: number;
  width: number;
  height: number;
  layout: LayoutType | null;
  isCollapsed: boolean;
  isSelected: boolean;
  isHovered: boolean;
};

const BUTTON_SIZE = 22;
const BUTTON_MARGIN = 8;

@customElement('edgeless-mindmap-collapse-buttons')
export class EdgelessMindmapCollapseButtons extends WithDisposable(LitElement) {
  static override styles = css`
    .mindmap-collapse-buttons-container {
      position: absolute;
      top: 0;
      left: 0;
      pointer-events: none;
      z-index: 1;
    }

    .collapse-button-wrapper {
      width: 22px;
      height: 22px;
      position: absolute;
      z-index: 1;
      pointer-events: auto;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .collapse-button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: clamp(18px, 2.5vmin, 22px);
      height: clamp(18px, 2.5vmin, 22px);
      border-radius: 50%;
      cursor: pointer;
      pointer-events: auto;
      transition: background 0.2s, box-shadow 0.15s, color 0.15s;
      border: 1px solid var(--affine-border-color);
      box-shadow: var(--affine-shadow-1);
      background: var(--affine-white);
    }

    .collapse-button:hover {
      border: 1px solid var(--affine-white-10);
      box-shadow: var(--affine-shadow-1);
    }

    .collapse-button.expanded {
      background: var(--affine-primary-color);
      border-color: var(--affine-primary-color);
      color: var(--affine-white);
      font-size: 11px;
      font-weight: 600;
    }

    .collapse-button.expanded:hover {
      background: var(--affine-primary-color);
      opacity: 0.9;
    }
  `;

  @property({ attribute: false })
  accessor edgeless!: EdgelessRootBlockComponent;

  @state()
  private accessor _buttons: CollapseButtonInfo[] = [];

  @state()
  private accessor _selectedNodeId: string | null = null;

  @state()
  private accessor _hoveredNodeId: string | null = null;

  private _onPointerMove = (e: PointerEvent) => {
    const [modelX, modelY] = this.edgeless.service.viewport.toModelCoord(
      e.clientX,
      e.clientY
    );
    const zoom = this.edgeless.service.viewport.zoom;
    const buttonAreaModel = (BUTTON_SIZE / 2 + BUTTON_MARGIN) / zoom;

    let foundNodeId: string | null = null;
    const elements = (this.edgeless.surfaceBlockModel as unknown as { getElementsByType(type: string): unknown[] }).getElementsByType('mindmap');

    for (const element of elements) {
      if (!(element instanceof MindmapElementModel)) continue;

      const mindmap = element;
      mindmap.traverse((node) => {
        if (foundNodeId) return;
        if (node.children.length === 0) return;
        if (node.detail.collapsed) return;

        const bound = node.element.elementBound;
        const layout = mindmap.getLayoutDir(node);
        const isLeft = layout === LayoutType.LEFT;

        const expandedX = isLeft ? bound.x - buttonAreaModel : bound.x;
        const expandedW = bound.w + buttonAreaModel;

        if (
          modelX >= expandedX &&
          modelX <= expandedX + expandedW &&
          modelY >= bound.y &&
          modelY <= bound.y + bound.h
        ) {
          foundNodeId = node.id;
        }
      }, { stopOnCollapse: true });
    }

    if (foundNodeId !== this._hoveredNodeId) {
      this._hoveredNodeId = foundNodeId;
      this._updateButtons();
    }
  };

  override connectedCallback() {
    super.connectedCallback();

    this._updateButtons();

    this._disposables.add(
      this.edgeless.service.viewport.viewportUpdated.on(() => {
        this.requestUpdate();
      })
    );

    this._disposables.add(
      this.edgeless.surfaceBlockModel.elementUpdated.on(() => {
        this._updateButtons();
      })
    );

    this._disposables.add(
      this.edgeless.surfaceBlockModel.elementAdded.on(() => {
        this._updateButtons();
      })
    );

    this._disposables.add(
      this.edgeless.surfaceBlockModel.elementRemoved.on(() => {
        this._updateButtons();
      })
    );

    this._disposables.add(
      this.edgeless.service.selection.slots.updated.on(() => {
        this._updateSelectedNode();
        this._updateButtons();
      })
    );

    const container = this.edgeless.host;
    container.addEventListener('pointermove', this._onPointerMove);
    this._disposables.add(() => {
      container.removeEventListener('pointermove', this._onPointerMove);
    });
  }

  private _updateSelectedNode() {
    const selection = this.edgeless.service.selection;
    const selectedElements = selection.selectedElements;

    if (selectedElements.length === 1) {
      const element = selectedElements[0];
      if (
        element instanceof ShapeElementModel &&
        element.group instanceof MindmapElementModel
      ) {
        this._selectedNodeId = element.id;
        return;
      }
    }

    this._selectedNodeId = null;
  }

  private _updateButtons() {
    const buttons: CollapseButtonInfo[] = [];
    const elements = (this.edgeless.surfaceBlockModel as unknown as { getElementsByType(type: string): unknown[] }).getElementsByType('mindmap');

    for (const element of elements) {
      if (!(element instanceof MindmapElementModel)) continue;

      const mindmap = element;
      mindmap.traverse((node) => {
        const hasChildren = node.children.length > 0;
        const isCollapsed = node.detail.collapsed ?? false;
        const isSelected = node.id === this._selectedNodeId;
        const isHovered = node.id === this._hoveredNodeId;

        const shouldShowButton = hasChildren && (isCollapsed || isSelected || isHovered);

        if (shouldShowButton) {
          const count = mindmap.getCollapseCount(node);
          const layout = mindmap.getLayoutDir(node);
          const bound = node.element.elementBound;

          buttons.push({
            id: `${mindmap.id}-${node.id}`,
            nodeId: node.id,
            mindmap,
            node,
            count,
            x: bound.x,
            y: bound.y,
            width: bound.w,
            height: bound.h,
            layout,
            isCollapsed,
            isSelected,
            isHovered,
          });
        }
      }, { stopOnCollapse: true });
    }

    this._buttons = buttons;
  }

  private _handleClick(info: CollapseButtonInfo) {
    info.mindmap.toggleCollapse(info.node, { layout: true });
    this._updateButtons();
  }

  override render() {
    if (this._buttons.length === 0) {
      return nothing;
    }

    const { viewport } = this.edgeless.service;
    const { zoom } = viewport;

    return html`
      <div class="mindmap-collapse-buttons-container">
        ${repeat(
          this._buttons,
          (info) => info.id,
          (info) => {
            const [nodeViewX, nodeViewY] = viewport.toViewCoord(info.x, info.y);
            const nodeViewWidth = info.width * zoom;
            const nodeViewHeight = info.height * zoom;

            const isLeft = info.layout === LayoutType.LEFT;

            let buttonX: number;
            const buttonY = nodeViewY + nodeViewHeight / 2;

            if (isLeft) {
              buttonX = nodeViewX - BUTTON_MARGIN;
            } else {
              buttonX = nodeViewX + nodeViewWidth + BUTTON_MARGIN;
            }

            let transform = `translate(${buttonX - BUTTON_SIZE / 2}px, ${buttonY - BUTTON_SIZE / 2}px)`;
            if (isLeft) {
              transform += ` scale(-1)`;
            }

            const buttonClass = info.isCollapsed
              ? 'collapse-button expanded'
              : 'collapse-button';

            const nodeElement = info.node.element as { stroke?: string };
            const nodeColor = nodeElement.stroke ?? '--affine-brand-color';

            return html`
              <div
                class="collapse-button-wrapper"
                style=${styleMap({
                  transform,
                  transformOrigin: 'left top',
                })}
              >
                <div
                  class=${buttonClass}
                  @pointerdown=${(e: PointerEvent) => {
                    e.stopPropagation();
                    this._handleClick(info);
                  }}
                >
                  ${info.isCollapsed ? info.count : getCollapseChevronIcon(nodeColor)}
                  <affine-tooltip tip-position=${isLeft ? 'left' : 'right'} .offset=${8}>
                    ${info.isCollapsed ? 'Expand' : 'Collapse'}
                  </affine-tooltip>
                </div>
              </div>
            `;
          }
        )}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'edgeless-mindmap-collapse-buttons': EdgelessMindmapCollapseButtons;
  }
}
